import { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Training,
  Theme,
  Evaluation,
  Flag,
  DuoToneCourseFilled,
  DuoToneAssessmentFilled,
  DuoToneVacancyFilled,
  Check,
  InformationFilled,
  DuoToneCompetenceFilled,
} from '@teo/components/icons';
import { IconTile } from '@teo/components';
import { ActiveCard } from './ActiveCard';
import { Blocked } from '@/components/Images/Blocked';
import { categoriesIcon } from '@/components/LearningPath/util';

const LEVEL_GAP = 250;
const NODE_VERTICAL_GAP = 280;

const X_RANDOM_RANGE = 90;

const Y_RANDOM_RANGE = 40;

const ZIGZAG_MIN = 40;
const ZIGZAG_MAX = 90;

const COLLISION_WIDTH = 70;
const COLLISION_HEIGHT = 170;
const COLLISION_ITERATIONS = 15;

const ORDER_SWEEPS = 12;

function hashInt(...nums) {
  let h = 2166136261 >>> 0;

  for (const value of nums) {
    const number = Number(value) || 0;

    h ^= number + 0x9e3779b9 + ((h << 6) >>> 0) + (h >>> 2);
    h >>>= 0;
  }

  return h >>> 0;
}

function mulberry32(seed) {
  return () => {
    let t = (seed += 0x6d2b79f5);

    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);

    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function randomBetween(random, min, max) {
  return min + (max - min) * random();
}

function getAverage(values) {
  if (!values.length) return null;

  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function resolveNodeCollisions(positions, rootId) {
  const entries = [...positions.entries()].map(([nodeId, position]) => ({
    nodeId,
    position: {
      x: position.x,
      y: position.y,
    },
  }));

  for (let iteration = 0; iteration < COLLISION_ITERATIONS; iteration++) {
    let hasCollision = false;

    entries.sort((first, second) => {
      if (first.position.x !== second.position.x) {
        return first.position.x - second.position.x;
      }

      return first.position.y - second.position.y;
    });

    for (let firstIndex = 0; firstIndex < entries.length; firstIndex++) {
      const first = entries[firstIndex];

      for (
        let secondIndex = firstIndex + 1;
        secondIndex < entries.length;
        secondIndex++
      ) {
        const second = entries[secondIndex];

        const distanceX = Math.abs(first.position.x - second.position.x);

        if (
          second.position.x > first.position.x &&
          distanceX >= COLLISION_WIDTH
        ) {
          break;
        }

        const distanceY = Math.abs(first.position.y - second.position.y);

        if (distanceX >= COLLISION_WIDTH || distanceY >= COLLISION_HEIGHT) {
          continue;
        }

        hasCollision = true;

        const pushDistance = COLLISION_HEIGHT - distanceY + 15;

        if (first.nodeId === rootId) {
          second.position.y += pushDistance;
          continue;
        }

        if (second.nodeId === rootId) {
          first.position.y -= pushDistance;
          continue;
        }

        if (first.position.y <= second.position.y) {
          first.position.y -= pushDistance / 2;
          second.position.y += pushDistance / 2;
        } else {
          first.position.y += pushDistance / 2;
          second.position.y -= pushDistance / 2;
        }
      }
    }

    if (!hasCollision) break;
  }

  entries.forEach(({ nodeId, position }) => {
    positions.set(nodeId, position);
  });
}

function calculateDepths(items, rootId, nodeById) {
  const depthById = new Map();
  const visiting = new Set();

  function getDepth(nodeId) {
    if (depthById.has(nodeId)) {
      return depthById.get(nodeId);
    }

    if (nodeId === rootId) {
      depthById.set(nodeId, 0);
      return 0;
    }

    if (visiting.has(nodeId)) {
      return 0;
    }

    visiting.add(nodeId);

    const node = nodeById.get(nodeId);
    const validParents =
      node?.roots?.filter((parentId) => nodeById.has(parentId)) || [];

    const depth =
      validParents.length === 0
        ? 0
        : Math.max(...validParents.map((parentId) => getDepth(parentId))) + 1;

    visiting.delete(nodeId);
    depthById.set(nodeId, depth);

    return depth;
  }

  items.forEach((item) => {
    getDepth(item.id);
  });

  return depthById;
}

function createOrderIndex(levels) {
  const orderIndex = new Map();

  levels.forEach((nodeIds) => {
    nodeIds.forEach((nodeId, index) => {
      orderIndex.set(nodeId, index);
    });
  });

  return orderIndex;
}

function sortLevelByNeighbours({
  nodeIds,
  neighbourById,
  neighbourOrder,
  stableOrder,
}) {
  return [...nodeIds].sort((firstId, secondId) => {
    const firstNeighbours = neighbourById.get(firstId) || [];
    const secondNeighbours = neighbourById.get(secondId) || [];

    const firstAverage = getAverage(
      firstNeighbours
        .map((id) => neighbourOrder.get(id))
        .filter((value) => value !== undefined)
    );

    const secondAverage = getAverage(
      secondNeighbours
        .map((id) => neighbourOrder.get(id))
        .filter((value) => value !== undefined)
    );

    if (firstAverage !== null && secondAverage !== null) {
      if (firstAverage !== secondAverage) {
        return firstAverage - secondAverage;
      }
    } else if (firstAverage !== null) {
      return -1;
    } else if (secondAverage !== null) {
      return 1;
    }

    return (stableOrder.get(firstId) ?? 0) - (stableOrder.get(secondId) ?? 0);
  });
}

function countCrossingsBetweenLevels({ leftLevel, rightLevel, edges }) {
  const leftOrder = new Map(leftLevel.map((nodeId, index) => [nodeId, index]));

  const rightOrder = new Map(
    rightLevel.map((nodeId, index) => [nodeId, index])
  );

  const levelEdges = edges
    .filter((edge) => leftOrder.has(edge.from) && rightOrder.has(edge.to))
    .map((edge) => ({
      fromIndex: leftOrder.get(edge.from),
      toIndex: rightOrder.get(edge.to),
    }));

  let crossings = 0;

  for (let firstIndex = 0; firstIndex < levelEdges.length; firstIndex++) {
    for (
      let secondIndex = firstIndex + 1;
      secondIndex < levelEdges.length;
      secondIndex++
    ) {
      const first = levelEdges[firstIndex];
      const second = levelEdges[secondIndex];

      const sourceOrder = first.fromIndex - second.fromIndex;

      const targetOrder = first.toIndex - second.toIndex;

      if (sourceOrder * targetOrder < 0) {
        crossings++;
      }
    }
  }

  return crossings;
}

function countAllCrossings(levels, edges, depthById) {
  let result = 0;

  for (let depth = 0; depth < levels.length - 1; depth++) {
    const adjacentEdges = edges.filter(
      (edge) =>
        depthById.get(edge.from) === depth &&
        depthById.get(edge.to) === depth + 1
    );

    result += countCrossingsBetweenLevels({
      leftLevel: levels[depth] || [],
      rightLevel: levels[depth + 1] || [],
      edges: adjacentEdges,
    });
  }

  return result;
}

function cloneLevels(levels) {
  return levels.map((level) => [...level]);
}

function optimiseLevelOrder({
  levels,
  parentsById,
  childrenById,
  edges,
  depthById,
  stableOrder,
}) {
  let bestLevels = cloneLevels(levels);
  let bestCrossings = countAllCrossings(bestLevels, edges, depthById);

  let currentLevels = cloneLevels(levels);

  for (let sweep = 0; sweep < ORDER_SWEEPS; sweep++) {
    for (let depth = 1; depth < currentLevels.length; depth++) {
      const previousOrder = new Map(
        currentLevels[depth - 1].map((nodeId, index) => [nodeId, index])
      );

      currentLevels[depth] = sortLevelByNeighbours({
        nodeIds: currentLevels[depth],
        neighbourById: parentsById,
        neighbourOrder: previousOrder,
        stableOrder,
      });
    }

    for (let depth = currentLevels.length - 2; depth >= 0; depth--) {
      const nextOrder = new Map(
        currentLevels[depth + 1].map((nodeId, index) => [nodeId, index])
      );

      currentLevels[depth] = sortLevelByNeighbours({
        nodeIds: currentLevels[depth],
        neighbourById: childrenById,
        neighbourOrder: nextOrder,
        stableOrder,
      });
    }

    const currentCrossings = countAllCrossings(currentLevels, edges, depthById);

    if (currentCrossings < bestCrossings) {
      bestCrossings = currentCrossings;
      bestLevels = cloneLevels(currentLevels);
    }
  }

  return bestLevels;
}

function buildReadableZigZagGraph(items, rootId) {
  const nodeById = new Map(items.map((item) => [item.id, item]));

  const stableOrder = new Map(items.map((item, index) => [item.id, index]));

  const parentsById = new Map();
  const childrenById = new Map();

  const edges = [];

  items.forEach((item) => {
    const validParents =
      item.roots?.filter((parentId) => nodeById.has(parentId)) || [];

    parentsById.set(item.id, validParents);

    validParents.forEach((parentId) => {
      if (!childrenById.has(parentId)) {
        childrenById.set(parentId, []);
      }

      childrenById.get(parentId).push(item.id);

      edges.push({
        from: parentId,
        to: item.id,
        mandatory: item.mandatory,
        progress: item.progress,
      });
    });
  });

  childrenById.forEach((childIds) => {
    childIds.sort((firstId, secondId) => {
      const first = nodeById.get(firstId);
      const second = nodeById.get(secondId);

      const firstMandatory = first?.layoutMandatory ?? first?.mandatory;
      const secondMandatory = second?.layoutMandatory ?? second?.mandatory;

      if (firstMandatory !== secondMandatory) {
        return firstMandatory ? -1 : 1;
      }

      return (stableOrder.get(firstId) ?? 0) - (stableOrder.get(secondId) ?? 0);
    });
  });

  const depthById = calculateDepths(items, rootId, nodeById);

  const maxDepth = Math.max(0, ...depthById.values());

  const levels = Array.from({ length: maxDepth + 1 }, () => []);

  items.forEach((item) => {
    const depth = depthById.get(item.id) ?? 0;

    if (!levels[depth]) {
      levels[depth] = [];
    }

    levels[depth].push(item.id);
  });

  levels.forEach((level) => {
    level.sort(
      (firstId, secondId) =>
        (stableOrder.get(firstId) ?? 0) - (stableOrder.get(secondId) ?? 0)
    );
  });

  const orderedLevels = optimiseLevelOrder({
    levels,
    parentsById,
    childrenById,
    edges,
    depthById,
    stableOrder,
  });

  const positions = new Map();

  const largestLevelSize = Math.max(
    1,
    ...orderedLevels.map((level) => level.length)
  );

  orderedLevels.forEach((level, depth) => {
    const levelHeight = Math.max(1, level.length) * NODE_VERTICAL_GAP;

    const largestHeight = largestLevelSize * NODE_VERTICAL_GAP;

    const levelStartY = (largestHeight - levelHeight) / 2;

    level.forEach((nodeId, index) => {
      const random = mulberry32(hashInt(nodeId, depth, index, 9257));

      const zigzagDirection = (depth + index) % 2 === 0 ? -1 : 1;

      const zigzagAmplitude = randomBetween(random, ZIGZAG_MIN, ZIGZAG_MAX);

      const randomX =
        depth === 0
          ? 0
          : randomBetween(random, -X_RANDOM_RANGE, X_RANDOM_RANGE);

      const randomY =
        depth === 0
          ? 0
          : randomBetween(random, -Y_RANDOM_RANGE, Y_RANDOM_RANGE);

      positions.set(nodeId, {
        x: depth * LEVEL_GAP + randomX,
        y:
          levelStartY +
          index * NODE_VERTICAL_GAP +
          zigzagDirection * zigzagAmplitude +
          randomY,
      });
    });
  });

  resolveNodeCollisions(positions, rootId);

  const nodes = items.map((item) => ({
    ...item,
    name: item.name || '',
    position: positions.get(item.id) || {
      x: 0,
      y: 0,
    },
  }));

  return {
    nodes,
    edges,
  };
}

const BlockIcon = ({ Icon, className }) => (
  <div className="rounded bg-grey-01 p-3">
    <Icon className={className} />
  </div>
);

export default function ConnectSection({
  lpGroup,
  items,
  activeItem,
  allCategories,
  setSelectNode,
  selectNode,
  auth,
  follow,
}) {
  const { t } = useTranslation();
  const [offset, setOffset] = useState({
    x: 0,
    y: 0,
  });

  const dragStart = useRef(null);
  const containerRef = useRef(null);
  const userHasPannedRef = useRef(false);

  const [containerSize, setContainerSize] = useState({
    width: 0,
    height: 0,
  });

  const nodeById = useMemo(() => {
    const map = new Map();

    items?.forEach((item) => {
      map.set(item.id, item);
    });

    return map;
  }, [items]);

  const { nodes, edges, dimensions } = useMemo(() => {
    if (!items?.length) {
      return {
        nodes: [],
        edges: [],
        dimensions: {
          width: 0,
          height: 0,
        },
      };
    }

    const root =
      items.find(
        (item) => !Array.isArray(item.roots) || item.roots.length === 0
      ) || items[0];

    const { nodes: layoutNodes, edges: layoutEdges } = buildReadableZigZagGraph(
      items,
      root.id
    );

    const nodeWidth = 150;
    const nodeHeight = 80;

    const minX = Math.min(...layoutNodes.map((node) => node.position.x));

    const maxX = Math.max(
      ...layoutNodes.map((node) => node.position.x + nodeWidth)
    );

    const minY = Math.min(...layoutNodes.map((node) => node.position.y));

    const maxY = Math.max(
      ...layoutNodes.map((node) => node.position.y + nodeHeight)
    );

    const paddingX = 260;
    const paddingY = 360;

    const normalizedNodes = layoutNodes.map((node) => ({
      ...node,
      position: {
        x: node.position.x - minX + paddingX / 2,
        y: node.position.y - minY + paddingY / 2,
      },
    }));

    return {
      nodes: normalizedNodes,
      edges: layoutEdges,
      dimensions: {
        width: maxX - minX + paddingX,
        height: maxY - minY + paddingY,
      },
    };
  }, [items]);

  useEffect(() => {
    const updateContainerSize = () => {
      if (!containerRef.current) return;

      const { width, height } = containerRef.current.getBoundingClientRect();

      setContainerSize({
        width,
        height,
      });
    };

    updateContainerSize();

    window.addEventListener('resize', updateContainerSize);

    return () => {
      window.removeEventListener('resize', updateContainerSize);
    };
  }, []);

  function clampOffset(newX, newY, dims, container) {
    const minX = container.width - dims.width;

    const maxX = 0;

    const minY = container.height - dims.height;

    const maxY = 0;

    return {
      x:
        dims.width > container.width ? Math.max(minX, Math.min(maxX, newX)) : 0,
      y:
        dims.height > container.height
          ? Math.max(minY, Math.min(maxY, newY))
          : 0,
    };
  }

  useEffect(() => {
    if (
      !containerRef.current ||
      nodes.length === 0 ||
      containerSize.height === 0
    ) {
      return;
    }

    if (userHasPannedRef.current) return;

    const activeNode =
      (activeItem && nodes.find((node) => node.id === activeItem.id)) ||
      nodes[0];

    if (!activeNode) return;

    const nodeHalfWidth = 75;
    const nodeHalfHeight = 40;

    const targetX = activeNode.position.x + nodeHalfWidth;

    const targetY = activeNode.position.y + nodeHalfHeight;

    const containerCenterX = containerSize.width / 2;

    const containerCenterY = containerSize.height / 2;

    const newX = containerCenterX - targetX;

    const newY = containerCenterY - targetY;

    setOffset(clampOffset(newX, newY, dimensions, containerSize));
  }, [activeItem, nodes, dimensions, containerSize]);

  const applyNewOffset = (clientX, clientY) => {
    if (!dragStart.current) return;

    const newX = clientX - dragStart.current.x;

    const newY = clientY - dragStart.current.y;

    setOffset(clampOffset(newX, newY, dimensions, containerSize));
  };

  const onMouseMove = (event) => {
    if (!dragStart.current) return;

    userHasPannedRef.current = true;

    applyNewOffset(event.clientX, event.clientY);
  };

  const onMouseUp = () => {
    dragStart.current = null;

    window.removeEventListener('mousemove', onMouseMove);

    window.removeEventListener('mouseup', onMouseUp);
  };

  const onMouseDown = (event) => {
    dragStart.current = {
      x: event.clientX - offset.x,
      y: event.clientY - offset.y,
    };

    window.addEventListener('mousemove', onMouseMove);

    window.addEventListener('mouseup', onMouseUp);
  };

  const onTouchMove = (event) => {
    if (!dragStart.current) return;

    userHasPannedRef.current = true;

    event.preventDefault();

    const touch = event.touches[0];

    applyNewOffset(touch.clientX, touch.clientY);
  };

  const onTouchEnd = () => {
    dragStart.current = null;

    window.removeEventListener('touchmove', onTouchMove);

    window.removeEventListener('touchend', onTouchEnd);
  };

  const onTouchStart = (event) => {
    const touch = event.touches[0];

    dragStart.current = {
      x: touch.clientX - offset.x,
      y: touch.clientY - offset.y,
    };

    window.addEventListener('touchmove', onTouchMove, {
      passive: false,
    });

    window.addEventListener('touchend', onTouchEnd);
  };

  useEffect(() => {
    return () => {
      window.removeEventListener('mousemove', onMouseMove);

      window.removeEventListener('mouseup', onMouseUp);

      window.removeEventListener('touchmove', onTouchMove);

      window.removeEventListener('touchend', onTouchEnd);
    };
  }, []);

  function findRealParent(sourceItems, nodeId, visited = new Set()) {
    if (visited.has(nodeId)) {
      return null;
    }

    visited.add(nodeId);

    const node = sourceItems.find((item) => item.id == nodeId);

    if (!node) return null;

    if (node.subType !== 'milestone') {
      return node;
    }

    if (!node.roots?.length) {
      return null;
    }

    return findRealParent(sourceItems, node.roots[0], visited);
  }

  function getAllParentsById(sourceItems, nodeId) {
    const byId = new Map(sourceItems.map((item) => [item.id, item]));

    const start = byId.get(nodeId);

    if (!start) return [];

    const result = [];
    const visited = new Set();

    function walk(node) {
      if (!node?.roots?.length) return;

      for (const parentId of node.roots) {
        if (visited.has(parentId)) {
          continue;
        }

        visited.add(parentId);

        const parent = byId.get(parentId);

        if (!parent) continue;

        result.push(parent);
        walk(parent);
      }
    }

    walk(start);

    return result;
  }

  function comesAfterPassNode(sourceItems, node) {
    const index = sourceItems.findIndex((item) => item.id === node?.id);

    if (index === 0) return true;
    if (!node?.roots?.length) return true;

    const allParents = getAllParentsById(sourceItems, node?.id);

    return allParents.every((parent) => {
      const realParent = findRealParent(sourceItems, parent.id);

      return realParent?.progress === 1 || !realParent?.blocking;
    });
  }

  function areAllPassed(sourceItems) {
    return sourceItems.every((item) => {
      if (item?.skippedByCompetences) return true;
      const hasProgress = item.progress === 1;

      const hasScore =
        item.maxScore === 0 && item.score === 0 && item?.isCorrected
          ? true
          : item.minScore > 0
          ? item.score / item.maxScore >= item.minScore
          : true;

      return hasProgress && hasScore && item?.isCorrected;
    });
  }

  const areMandatoryRootsDone = (node) => {
    const roots = node?.roots || [];

    if (roots.length === 0) return true;

    const allParents = getAllParentsById(items, node?.id).filter(
      (item) => item.mandatory !== false
    );

    if (allParents.length === 0) {
      return true;
    }

    return allParents.every((parentItem) => {
      const parent = nodeById.get(parentItem.id);

      if (!parent) return true;

      if (node?.subType === 'milestone') {
        const allBefore = getAllParentsById(items, node?.id);

        if (!areAllPassed(allBefore)) {
          return false;
        }
      }

      if (parent?.blocking && !parent?.notCompleted && !parent?.notPassed) {
        return true;
      }

      if (parent?.subType === 'milestone' && activeItem?.id === node?.id) {
        return true;
      }

      if (parent?.subType === 'milestone') {
        return true;
      }

      if (parent.notPassed === false) {
        return parent.progress === 1;
      }

      if (parent.notPassed === true) {
        const hasProgress = parent.progress === 1;

        const enoughScore =
          parent.maxScore > 0
            ? parent.score / parent.maxScore >= parent.minScore
            : false;

        return hasProgress && enoughScore;
      }

      return false;
    });
  };

  const isNodeAvailable = (node) => {
    if (!node) return false;

    return (
      lpGroup?.order === 'flexible' ||
      (areMandatoryRootsDone(node) && comesAfterPassNode(items, node))
    );
  };

  useEffect(() => {
    if (selectNode || !items?.length) {
      return;
    }

    const initialNode = activeItem || nodes[0];

    if (!initialNode) return;

    setSelectNode({
      ...initialNode,
      notBlock: lpGroup?.order === 'flexible' || isNodeAvailable(initialNode),
    });
  }, [selectNode, activeItem, nodes, items, lpGroup?.order]);

  if (!items?.length) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className="relative flex h-[552px] overflow-hidden md:h-[640px]"
    >
      {selectNode && (
        <ActiveCard
          selectNode={selectNode}
          auth={auth}
          allCategories={allCategories}
          items={items}
          follow={follow}
        />
      )}

      <div className="absolute left-0 top-0 h-full w-full">
        <div
          className="relative my-auto touch-none"
          onMouseDown={onMouseDown}
          onTouchStart={onTouchStart}
          style={{
            transform: `translate(${offset.x}px, ${offset.y}px)`,
            cursor: 'grab',
            minWidth: `${dimensions.width}px`,
            height: `${dimensions.height}px`,
            minHeight: '100%',
          }}
        >
          <svg
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              top: '-15px',
              left: '-50px',
            }}
          >
            {edges.map((edge, index) => {
              const from = nodes.find((node) => node.id === edge.from);

              const to = nodes.find((node) => node.id === edge.to);

              const modelTo = nodeById.get(edge.to);

              const modelFrom = nodeById.get(edge.from);

              const edgeActive =
                lpGroup?.order === 'flexible' || isNodeAvailable(modelTo);

              const edgeCompleted =
                lpGroup?.order === 'flexible' || modelFrom?.progress === 1;

              if (!from || !to) {
                return null;
              }

              return (
                <line
                  key={`line-${edge.from}-${edge.to}-${index}`}
                  x1={from.position.x + 75}
                  y1={from.position.y + 40}
                  x2={to.position.x + 75}
                  y2={to.position.y + 40}
                  stroke={
                    lpGroup?.order === 'flexible' || edgeActive
                      ? '#3b82f6'
                      : edgeCompleted
                      ? '#1E1E202E'
                      : '#1E1E202E'
                  }
                  strokeWidth={
                    lpGroup?.order === 'flexible' || edgeActive
                      ? 2
                      : from.mandatory && to.mandatory
                      ? 1
                      : 1
                  }
                  strokeDasharray={to.mandatory ? '0' : '6,4'}
                />
              );
            })}
          </svg>

          {nodes.map((node, index) => {
            let LearningPathIcon;

            if (node?.tags && node?.type === 'learningpath') {
              const learningPathTag = allCategories?.find(
                (category) => category?.label === node?.tags.split(':')[1]
              );

              LearningPathIcon = categoriesIcon(learningPathTag?.icon);
            } else {
              LearningPathIcon = Theme;
            }

            const available = isNodeAvailable(node);

            const canOpen =
              lpGroup?.order === 'flexible' || index === 0 || available;

            return (
              <div
                key={node.id}
                className={`absolute flex ${
                  selectNode?.id === node.id
                    ? canOpen
                      ? 'border-[2px] border-secondary-04'
                      : 'border-[2px] border-grey-07'
                    : ''
                }`}
                style={{
                  cursor: 'pointer',
                  transform: 'rotate(-45deg) skew(15deg, 15deg)',
                  boxShadow: '0 1px 4px 0 rgb(0 0 0 / 20%)',
                  top: `${node.position.y}px`,
                  left: `${node.position.x}px`,
                  borderRadius: '4px',
                }}
                onMouseDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();

                  setSelectNode({
                    ...node,
                    notBlock: lpGroup?.order === 'flexible' || available,
                  });
                }}
              >
                {canOpen ? (
                  <>
                    {node?.skippedByCompetences && node?.progress !== 1 && (
                      <div
                        className="absolute -right-2 -top-2 flex h-5 w-5 rounded-[4px] bg-primary-05"
                        title={t('pages.learning_path.conditions.skip_badge')}
                      >
                        <DuoToneCompetenceFilled className="m-auto w-4 fill-white text-white" />
                      </div>
                    )}
                    {node?.progress === 1 &&
                      (node.maxScore > 0 ? (
                        node.score / node.maxScore >= node.minScore ? (
                          <div className="absolute -right-2 -top-2 flex h-5 w-5 rounded-[4px] bg-secondary-04">
                            <Check className="m-auto w-4 text-white" />
                          </div>
                        ) : (
                          <div className="absolute -right-2 -top-2 flex h-5 w-5 rounded-[4px] bg-secondary-04">
                            <InformationFilled className="m-auto w-5 text-white" />
                          </div>
                        )
                      ) : node?.isCorrected ? (
                        <div className="absolute -right-2 -top-2 flex h-5 w-5 rounded-[4px] bg-secondary-04">
                          <Check className="m-auto w-4 text-white" />
                        </div>
                      ) : (
                        <div className="absolute -right-2 -top-2 flex h-5 w-5 rounded-[4px] bg-secondary-04">
                          <InformationFilled className="m-auto w-5 text-white" />
                        </div>
                      ))}
                    <IconTile
                      Icon={
                        node.subType === 'training'
                          ? Training
                          : node.subType === 'learningpath'
                          ? LearningPathIcon
                          : node.subType === 'milestone'
                          ? Flag
                          : node.subType === 'evaluation'
                          ? Evaluation
                          : node.subType === 'course'
                          ? DuoToneCourseFilled
                          : node.subType === 'assessment'
                          ? DuoToneAssessmentFilled
                          : node.subType === 'leervacature'
                          ? DuoToneVacancyFilled
                          : DuoToneCourseFilled
                      }
                      // imageUrl={node.picture}
                      color={
                        node.subType === 'training'
                          ? 'green'
                          : node.subType === 'learningpath'
                          ? 'dark_blue'
                          : node.subType === 'milestone'
                          ? 'blue'
                          : node.subType === 'evaluation'
                          ? 'purple'
                          : node.subType === 'course'
                          ? 'yellow'
                          : node.subType === 'assessment'
                          ? 'red'
                          : node.subType === 'leervacature'
                          ? 'yellow'
                          : 'yellow'
                      }
                      size="small_md"
                      className={
                        node.subType === 'course' || node.subType === 'import'
                          ? 'fill-primary-06 text-primary-01'
                          : node.subType === 'assessment'
                          ? 'fill-tint-red-dark text-tint-red-light'
                          : node.subType === 'leervacature'
                          ? 'fill-error-04 text-error-04'
                          : ''
                      }
                    />
                  </>
                ) : (
                  <>
                    <div className="absolute -right-2 -top-2 flex h-5 w-5 rounded-[4px] bg-grey-08">
                      <Blocked className="m-auto w-[10px] text-white" />
                    </div>
                    <BlockIcon
                      Icon={
                        node.subType === 'training'
                          ? Training
                          : node.subType === 'learningpath'
                          ? Theme
                          : node.subType === 'milestone'
                          ? Flag
                          : node.subType === 'evaluation'
                          ? Evaluation
                          : node.subType === 'course'
                          ? DuoToneCourseFilled
                          : node.subType === 'assessment'
                          ? DuoToneAssessmentFilled
                          : node.subType === 'leervacature'
                          ? DuoToneVacancyFilled
                          : DuoToneCourseFilled
                      }
                      className={`h-6 w-6 ${
                        node?.type === 'course'
                          ? node.subType === 'leervacature'
                            ? 'fill-grey-04 text-grey-04'
                            : 'fill-grey-04 text-grey-01'
                          : 'text-grey-04'
                      }`}
                    />
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
